import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ENDPOINTS } from '../config';
import { supabase } from '../lib/supabase';

export function PaymentConfirmation() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const handleConfirmation = async () => {
      const setupIntentId = searchParams.get('setup_intent_id');
      const paymentIntentId = searchParams.get('payment_intent');
      
      if (setupIntentId) {
        // Handle bank account setup completion
        try {
          const { data: sessionData } = await supabase.auth.getSession();
          const token = sessionData.session?.access_token;

          if (!token) {
            setStatus('error');
            setMessage('Authentication required. Please log in and try again.');
            return;
          }

          const response = await fetch(ENDPOINTS.updateBankAccountSetupIntent, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ setupIntentId }),
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || 'Failed to complete bank account setup');
          }

          const data = await response.json();

          if (data.status === 'succeeded') {
            setStatus('success');
            setMessage('Bank account successfully verified and connected! You can now use it for payments.');
            
            // Redirect to dashboard after a delay
            setTimeout(() => {
              navigate('/dashboard');
            }, 3000);
          } else {
            setStatus('error');
            setMessage(`Setup verification ${data.status}. Please try again.`);
          }
        } catch (error) {
          console.error('Error completing bank account setup:', error);
          setStatus('error');
          setMessage(error instanceof Error ? error.message : 'Failed to complete bank account setup');
        }
      } else if (paymentIntentId) {
        // Handle payment completion (if needed in the future)
        setStatus('success');
        setMessage('Payment completed successfully!');
        
        setTimeout(() => {
          navigate('/invoices');
        }, 3000);
      } else {
        setStatus('error');
        setMessage('No confirmation details found in the URL.');
      }
    };

    if (user) {
      handleConfirmation();
    }
  }, [searchParams, navigate, user]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="text-center">
            {status === 'loading' && (
              <div>
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <h2 className="mt-4 text-base sm:text-lg font-medium text-gray-900">Processing...</h2>
                <p className="mt-2 text-sm text-gray-600">
                  Please wait while we complete your request.
                </p>
              </div>
            )}

            {status === 'success' && (
              <div>
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                  <svg
                    className="h-6 w-6 text-green-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h2 className="mt-4 text-base sm:text-lg font-medium text-gray-900">Success!</h2>
                <p className="mt-2 text-sm text-gray-600">{message}</p>
                <p className="mt-2 text-xs text-gray-500">
                  You will be redirected automatically in a few seconds.
                </p>
              </div>
            )}

            {status === 'error' && (
              <div>
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <svg
                    className="h-6 w-6 text-red-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </div>
                <h2 className="mt-4 text-base sm:text-lg font-medium text-gray-900">Error</h2>
                <p className="mt-2 text-sm text-gray-600">{message}</p>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="mt-4 w-full inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Return to Dashboard
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 